/**
 * cartCheckoutSettings.ts
 * ─────────────────────────
 * Types, defaults, and React hook for the Cart & Checkout
 * customisation system.  All settings are persisted as a single
 * JSON blob in the content-editing layer under key `__ccSettings`.
 */

import { useMemo, useCallback } from "react";
import { useContentEditingSafe } from "../components/shared/ContentEditingContext";

/* ================================================================
   1. TYPES
   ================================================================ */

export interface ShippingMethodConfig {
  id: string;
  name: string;
  description: string;
  price: number;
  estimatedDays: string;
  icon: string; // lucide icon name key
  enabled: boolean;
}

export interface PaymentMethodConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  enabled: boolean;
}

export interface TrustBadgeConfig {
  id: string;
  text: string;
  icon: string;
  enabled: boolean;
}

export interface FormFieldConfig {
  id: string;
  label: string;
  placeholder: string;
  type: "text" | "tel" | "email";
  required: boolean;
  visible: boolean;
}

export interface CartCheckoutSettings {
  /* ── 1. Shipping ── */
  shippingMethods: ShippingMethodConfig[];
  freeShippingThreshold: number;
  showShippingSelector: boolean;

  /* ── 2. Payment ── */
  paymentMethods: PaymentMethodConfig[];
  paymentSecurityText: string;
  showPaymentMethods: boolean;

  /* ── 3. Coupon ── */
  couponEnabled: boolean;
  couponPlaceholder: string;
  couponSuccessMessage: string;
  couponErrorMessage: string;
  couponDiscountPercent: number;

  /* ── 4. Form fields ── */
  formFields: FormFieldConfig[];
  showAlternateAddress: boolean;
  showOrderNotes: boolean;

  /* ── 6. Trust Badges ── */
  trustBadges: TrustBadgeConfig[];
  showTrustBadges: boolean;
  showPaymentIcons: boolean;

  /* ── 7. Design ── */
  ctaColor: string;
  ctaColorCheckout: string;
  heroGradientFrom: string;
  heroGradientTo: string;
  checkoutLayout: "two-column" | "single-column";
  showProgressSteps: boolean;

  /* ── 8. Messages ── */
  orderSuccessMessage: string;
  redirectAfterOrder: "home" | "catalog" | "thank-you";
  emptyCartTitle: string;
  emptyCartDescription: string;
  emptyCartCta: string;
  showUpsell: boolean;
  upsellTitle: string;
}

/* ================================================================
   2. DEFAULTS
   ================================================================ */

export const DEFAULT_SETTINGS: CartCheckoutSettings = {
  /* ── 1. Shipping ── */
  shippingMethods: [
    {
      id: "free",
      name: "\u05DE\u05E9\u05DC\u05D5\u05D7 \u05D7\u05D9\u05E0\u05DD",
      description: "\u05E2\u05D3 7 \u05D9\u05DE\u05D9 \u05E2\u05E1\u05E7\u05D9\u05DD \u2022 \u05D3\u05D5\u05D0\u05E8 \u05D9\u05E9\u05E8\u05D0\u05DC",
      price: 0,
      estimatedDays: "5-7",
      icon: "truck",
      enabled: true,
    },
    {
      id: "express",
      name: "\u05DE\u05E9\u05DC\u05D5\u05D7 \u05D0\u05E7\u05E1\u05E4\u05E8\u05E1",
      description: "\u05E2\u05D3 3 \u05D9\u05DE\u05D9 \u05E2\u05E1\u05E7\u05D9\u05DD \u2022 \u05E9\u05DC\u05D9\u05D7 \u05E2\u05D3 \u05D4\u05D1\u05D9\u05EA",
      price: 75,
      estimatedDays: "1-3",
      icon: "zap",
      enabled: true,
    },
    {
      id: "pickup",
      name: "\u05D0\u05D9\u05E1\u05D5\u05E3 \u05E2\u05E6\u05DE\u05D9",
      description: "\u05D0\u05D9\u05E1\u05D5\u05E3 \u05DE\u05D4\u05E1\u05E0\u05D9\u05E3/\u05D7\u05E0\u05D5\u05EA",
      price: 0,
      estimatedDays: "0",
      icon: "store",
      enabled: false,
    },
  ],
  freeShippingThreshold: 300,
  showShippingSelector: true,

  /* ── 2. Payment ── */
  paymentMethods: [
    { id: "credit", name: "\u05DB\u05E8\u05D8\u05D9\u05E1 \u05D0\u05E9\u05E8\u05D0\u05D9", description: "Visa / Mastercard / Amex", icon: "credit-card", enabled: true },
    { id: "bit", name: "Bit", description: "\u05EA\u05E9\u05DC\u05D5\u05DD \u05D1\u05D0\u05E4\u05DC\u05D9\u05E7\u05E6\u05D9\u05D9\u05EA Bit", icon: "smartphone", enabled: true },
    { id: "paypal", name: "PayPal", description: "\u05EA\u05E9\u05DC\u05D5\u05DD \u05DE\u05D0\u05D5\u05D1\u05D8\u05D7 \u05D3\u05E8\u05DA PayPal", icon: "wallet", enabled: false },
    { id: "transfer", name: "\u05D4\u05E2\u05D1\u05E8\u05D4 \u05D1\u05E0\u05E7\u05D0\u05D9\u05EA", description: "\u05D4\u05E2\u05D1\u05E8\u05D4 \u05D9\u05E9\u05D9\u05E8\u05D4 \u05DC\u05D7\u05E9\u05D1\u05D5\u05DF \u05D4\u05D7\u05D1\u05E8\u05D4", icon: "building", enabled: false },
  ],
  paymentSecurityText: "\u05D4\u05EA\u05E9\u05DC\u05D5\u05DD \u05DE\u05D0\u05D5\u05D1\u05D8\u05D7 \u05D1\u05D4\u05E6\u05E4\u05E0\u05EA SSL 256-bit",
  showPaymentMethods: true,

  /* ── 3. Coupon ── */
  couponEnabled: true,
  couponPlaceholder: "\u05D9\u05E9 \u05DC\u05DB\u05DD \u05E7\u05D5\u05D3 \u05D4\u05E0\u05D7\u05D4?",
  couponSuccessMessage: "\u05E7\u05D5\u05D3 \u05E7\u05D5\u05E4\u05D5\u05DF \u05D4\u05D5\u05D7\u05DC \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4!",
  couponErrorMessage: "\u05E7\u05D5\u05D3 \u05E7\u05D5\u05E4\u05D5\u05DF \u05DC\u05D0 \u05EA\u05E7\u05D9\u05DF",
  couponDiscountPercent: 10,

  /* ── 4. Form fields ── */
  formFields: [
    { id: "firstName", label: "\u05E9\u05DD \u05E4\u05E8\u05D8\u05D9", placeholder: "\u05D9\u05D5\u05E1\u05D9", type: "text", required: true, visible: true },
    { id: "lastName", label: "\u05E9\u05DD \u05DE\u05E9\u05E4\u05D7\u05D4", placeholder: "\u05D9\u05E9\u05E8\u05D0\u05DC\u05D9", type: "text", required: true, visible: true },
    { id: "company", label: "\u05E9\u05DD \u05D7\u05D1\u05E8\u05D4 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)", placeholder: "\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9", type: "text", required: false, visible: true },
    { id: "street", label: "\u05E8\u05D7\u05D5\u05D1 \u05D5\u05DE\u05E1\u05E4\u05E8 \u05D1\u05D9\u05EA", placeholder: "\u05E8\u05D7\u05D5\u05D1 \u05D4\u05E8\u05E6\u05DC 15", type: "text", required: true, visible: true },
    { id: "apartment", label: "\u05D3\u05D9\u05E8\u05D4, \u05E7\u05D5\u05DE\u05D4, \u05DB\u05E0\u05D9\u05E1\u05D4", placeholder: "\u05D3\u05D9\u05E8\u05D4 4, \u05E7\u05D5\u05DE\u05D4 2", type: "text", required: false, visible: true },
    { id: "zipCode", label: "\u05DE\u05D9\u05E7\u05D5\u05D3", placeholder: "0000000", type: "text", required: false, visible: true },
    { id: "city", label: "\u05E2\u05D9\u05E8 / \u05D9\u05E9\u05D5\u05D1", placeholder: "\u05EA\u05DC \u05D0\u05D1\u05D9\u05D1", type: "text", required: true, visible: true },
    { id: "phone", label: "\u05D8\u05DC\u05E4\u05D5\u05DF", placeholder: "050-0000000", type: "tel", required: true, visible: true },
    { id: "email", label: "\u05D0\u05D9\u05DE\u05D9\u05D9\u05DC", placeholder: "your@email.com", type: "email", required: true, visible: true },
    { id: "idNumber", label: "\u05EA.\u05D6. / \u05D7.\u05E4.", placeholder: "000000000", type: "text", required: false, visible: false },
  ],
  showAlternateAddress: true,
  showOrderNotes: true,

  /* ── 6. Trust Badges ── */
  trustBadges: [
    { id: "secure", text: "\u05EA\u05E9\u05DC\u05D5\u05DD \u05DE\u05D0\u05D5\u05D1\u05D8\u05D7", icon: "shield-check", enabled: true },
    { id: "returns", text: "\u05D0\u05D7\u05E8\u05D9\u05D5\u05EA 30 \u05D9\u05D5\u05DD", icon: "rotate-ccw", enabled: true },
    { id: "fast", text: "\u05DE\u05E9\u05DC\u05D5\u05D7 \u05DE\u05D4\u05D9\u05E8", icon: "truck", enabled: true },
    { id: "support", text: "\u05EA\u05DE\u05D9\u05DB\u05D4 24/7", icon: "headphones", enabled: true },
    { id: "quality", text: "\u05D0\u05D9\u05DB\u05D5\u05EA \u05DE\u05D5\u05D1\u05D8\u05D7\u05EA", icon: "award", enabled: false },
  ],
  showTrustBadges: true,
  showPaymentIcons: true,

  /* ── 7. Design ── */
  ctaColor: "#e74c3c",
  ctaColorCheckout: "#e74c3c",
  heroGradientFrom: "#001C32",
  heroGradientTo: "#052b7a",
  checkoutLayout: "two-column",
  showProgressSteps: true,

  /* ── 8. Messages ── */
  orderSuccessMessage: "\u05D4\u05D4\u05D6\u05DE\u05E0\u05D4 \u05E0\u05E9\u05DC\u05D7\u05D4 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4! \u05EA\u05D5\u05D3\u05D4 \u05E8\u05D1\u05D4 \u05E2\u05DC \u05D4\u05E8\u05DB\u05D9\u05E9\u05D4.",
  redirectAfterOrder: "home",
  emptyCartTitle: "\u05D4\u05E1\u05DC \u05E8\u05D9\u05E7",
  emptyCartDescription: "\u05D8\u05E8\u05DD \u05D4\u05D5\u05E1\u05E4\u05EA\u05DD \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05DC\u05E1\u05DC \u2014 \u05E2\u05D9\u05D9\u05E0\u05D5 \u05D1\u05E7\u05D8\u05DC\u05D5\u05D2 \u05D4\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05E9\u05DC\u05E0\u05D5.",
  emptyCartCta: "\u05D7\u05D6\u05E8\u05D5 \u05DC\u05E7\u05D8\u05DC\u05D5\u05D2",
  showUpsell: false,
  upsellTitle: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05E9\u05D0\u05D5\u05DC\u05D9 \u05D9\u05E2\u05E0\u05D9\u05D9\u05E0\u05D5 \u05D0\u05D5\u05EA\u05DA",
};

/* ================================================================
   3. STORAGE KEY
   ================================================================ */

const SETTINGS_KEY = "__ccSettings";

/* ================================================================
   4. HOOK
   ================================================================ */

export function useCartCheckoutSettings(): {
  settings: CartCheckoutSettings;
  updateSettings: (patch: Partial<CartCheckoutSettings>) => void;
  resetSettings: () => void;
} {
  const ctx = useContentEditingSafe();

  const settings = useMemo<CartCheckoutSettings>(() => {
    if (!ctx) return DEFAULT_SETTINGS;
    const raw = ctx.getContent(SETTINGS_KEY, "");
    if (!raw) return DEFAULT_SETTINGS;
    try {
      const parsed = JSON.parse(raw) as Partial<CartCheckoutSettings>;
      return { ...DEFAULT_SETTINGS, ...parsed };
    } catch {
      return DEFAULT_SETTINGS;
    }
  }, [ctx]);

  const updateSettings = useCallback(
    (patch: Partial<CartCheckoutSettings>) => {
      if (!ctx) return;
      const merged = { ...settings, ...patch };
      ctx.saveContent(SETTINGS_KEY, JSON.stringify(merged));
    },
    [ctx, settings],
  );

  const resetSettings = useCallback(() => {
    if (!ctx) return;
    ctx.resetContent(SETTINGS_KEY);
  }, [ctx]);

  return { settings, updateSettings, resetSettings };
}

/* ================================================================
   5. ICON MAP  (string key → lucide-react component)
   ================================================================ */

export const ICON_OPTIONS = [
  { value: "truck", label: "\u05DE\u05E9\u05D0\u05D9\u05EA" },
  { value: "zap", label: "\u05D1\u05E8\u05E7" },
  { value: "store", label: "\u05D7\u05E0\u05D5\u05EA" },
  { value: "bike", label: "\u05D0\u05D5\u05E4\u05E0\u05D9\u05D9\u05DD" },
  { value: "package", label: "\u05D7\u05D1\u05D9\u05DC\u05D4" },
  { value: "credit-card", label: "\u05DB\u05E8\u05D8\u05D9\u05E1 \u05D0\u05E9\u05E8\u05D0\u05D9" },
  { value: "wallet", label: "\u05D0\u05E8\u05E0\u05E7" },
  { value: "building", label: "\u05D1\u05E0\u05D9\u05D9\u05DF" },
  { value: "smartphone", label: "\u05E1\u05DE\u05D0\u05E8\u05D8\u05E4\u05D5\u05DF" },
  { value: "banknote", label: "\u05E9\u05D8\u05E8" },
  { value: "shield-check", label: "\u05DE\u05D2\u05DF \u05D0\u05D9\u05E9\u05D5\u05E8" },
  { value: "rotate-ccw", label: "\u05D4\u05D7\u05D6\u05E8\u05D4" },
  { value: "headphones", label: "\u05D0\u05D5\u05D6\u05E0\u05D9\u05D5\u05EA" },
  { value: "award", label: "\u05E4\u05E8\u05E1" },
  { value: "lock", label: "\u05DE\u05E0\u05E2\u05D5\u05DC" },
  { value: "clock", label: "\u05E9\u05E2\u05D5\u05DF" },
] as const;

export const CTA_COLOR_PRESETS = [
  { value: "#e74c3c", label: "\u05D0\u05D3\u05D5\u05DD" },
  { value: "#052b7a", label: "\u05DB\u05D7\u05D5\u05DC \u05DB\u05D4\u05D4" },
  { value: "#10b981", label: "\u05D9\u05E8\u05D5\u05E7" },
  { value: "#f59e0b", label: "\u05DB\u05EA\u05D5\u05DD" },
  { value: "#8b5cf6", label: "\u05E1\u05D2\u05D5\u05DC" },
  { value: "#1a1a2e", label: "\u05DB\u05D4\u05D4 \u05E2\u05DE\u05D5\u05E7" },
  { value: "#ea580c", label: "\u05DB\u05EA\u05D5\u05DD \u05D1\u05D5\u05E2\u05E8" },
] as const;

export const HERO_GRADIENT_PRESETS = [
  { from: "#001C32", to: "#052b7a", label: "\u05DB\u05D7\u05D5\u05DC \u05E7\u05DC\u05D0\u05E1\u05D9" },
  { from: "#1a1a2e", to: "#16213e", label: "\u05DB\u05D4\u05D4 \u05E2\u05DE\u05D5\u05E7" },
  { from: "#0f172a", to: "#1e3a5f", label: "\u05DC\u05D9\u05DC\u05D4" },
  { from: "#064e3b", to: "#0d9488", label: "\u05D9\u05E8\u05D5\u05E7" },
  { from: "#4c1d95", to: "#6d28d9", label: "\u05E1\u05D2\u05D5\u05DC" },
  { from: "#7c2d12", to: "#c2410c", label: "\u05D7\u05D5\u05DD" },
] as const;
